import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Button } from '@getro/rombo';
import { ThumbsUp, ThumbsDown, Check } from 'lucide-react';

// Analytics are emitted via the injected `onTrack` callback — no HTTP write.
const LEGS = ['primary', 'secondary'];

const EVENT_RATING = 'contacts:relationship_strength:feedback_rating';
const EVENT_SUBMITTED = 'contacts:relationship_strength:feedback_submitted';

const SENT_COPY = "Thanks! We'll use your feedback to improve the product.";
const DEFAULT_PROMPT = 'How does this rating feel?';
const CLICKED_PROMPT = 'How would you classify this and why is the rating off?';
const EMPTY_COMMENT_ERROR = 'Please add a comment before sending.';

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`;

const DefaultRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const Prompt = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.neutral[500]};
`;

const ThumbButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  padding: 0;
  background: none;
  border: 0;
  color: ${({ theme }) => theme.colors.neutral[500]};
  cursor: pointer;
`;

const ClickedWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  width: 100%;
`;

const ClickedPrompt = styled.label`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.neutral[500]};
`;

const Textarea = styled.textarea`
  width: 100%;
  height: 80px;
  resize: none;
  padding: 8px;
  font: inherit;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.dark};
  border: 1px solid ${({ theme }) => theme.colors.border.subtle};
  border-radius: 4px;
`;

const ErrorText = styled.p`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.error};
  margin: 0;
`;

const SentRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  color: ${({ theme }) => theme.colors.neutral[500]};
  font-size: 12px;
`;

const RelationshipStrengthFeedback = ({
  contactId,
  pathId,
  leg,
  relationshipStrength,
  signalsMet,
  onTrack,
  className,
}) => {
  const [state, setState] = useState('default');
  const [rating, setRating] = useState(null);
  const [comment, setComment] = useState('');
  const [error, setError] = useState(false);

  const basePayload = {
    targetContactId: contactId,
    pathId,
    relationshipStrength,
    hop: leg === 'primary' ? 'first' : 'second',
    signalsMet,
  };

  const emit = (event, extra) => onTrack(event, { ...basePayload, ...extra });

  const handleRate = (value) => {
    setRating(value);
    emit(EVENT_RATING, { rating: value });
    setState(value === 'up' ? 'sent' : 'clicked');
  };

  const handleCommentChange = (e) => {
    setComment(e.target.value);
    if (error && e.target.value.trim()) setError(false);
  };

  const handleSend = () => {
    if (!comment.trim()) {
      setError(true);
      return;
    }
    emit(EVENT_SUBMITTED, { rating, message: comment });
    setState('sent');
  };

  return (
    <Wrap className={className} data-state={state} data-leg={leg}>
      {state === 'default' && (
        <DefaultRow>
          <Prompt>{DEFAULT_PROMPT}</Prompt>
          <ThumbButton type="button" aria-label="Rate up" data-action="thumbs-up" onClick={() => handleRate('up')}>
            <ThumbsUp size={12} strokeWidth={1.5} />
          </ThumbButton>
          <ThumbButton
            type="button"
            aria-label="Rate down"
            data-action="thumbs-down"
            onClick={() => handleRate('down')}
          >
            <ThumbsDown size={12} strokeWidth={1.5} />
          </ThumbButton>
        </DefaultRow>
      )}

      {state === 'clicked' && (
        <ClickedWrap>
          <ClickedPrompt htmlFor={`rs-feedback-${pathId}-${leg}`}>{CLICKED_PROMPT}</ClickedPrompt>
          <Textarea
            id={`rs-feedback-${pathId}-${leg}`}
            value={comment}
            placeholder="Please include the relationship strength you'd give it and why"
            onChange={handleCommentChange}
            maxLength={2000}
            data-testid="rs-feedback-textarea"
          />
          {error && <ErrorText data-testid="rs-feedback-error">{EMPTY_COMMENT_ERROR}</ErrorText>}
          <Button type="button" onClick={handleSend} data-action="send" size="small">
            Send
          </Button>
        </ClickedWrap>
      )}

      {state === 'sent' && (
        <SentRow>
          <Check size={14} strokeWidth={2} aria-hidden="true" />
          {SENT_COPY}
        </SentRow>
      )}
    </Wrap>
  );
};

RelationshipStrengthFeedback.propTypes = {
  contactId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  pathId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  leg: PropTypes.oneOf(LEGS).isRequired,
  relationshipStrength: PropTypes.string,
  signalsMet: PropTypes.arrayOf(PropTypes.string),
  onTrack: PropTypes.func.isRequired,
  className: PropTypes.string,
};

RelationshipStrengthFeedback.defaultProps = {
  relationshipStrength: undefined,
  signalsMet: undefined,
  className: undefined,
};

export { LEGS, EVENT_RATING, EVENT_SUBMITTED, SENT_COPY };
export default RelationshipStrengthFeedback;
